import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { useAuth } from '../../features/auth/AuthProvider.js';
import type { SharedDoc } from '../../pages/DocSign.js';
import { SignaturePad, type SignaturePadHandle } from '../primitives/SignaturePad.js';

interface WorkerLite {
  id: string;
  name: string;
  role: string;
}

interface Props {
  onClose: () => void;
  active: SharedDoc;
  workers: WorkerLite[];
}

export function WorkerSignModal({ onClose, active, workers }: Props) {
  const qc = useQueryClient();
  const auth = useAuth();
  const [confirmName, setConfirmName] = useState('');
  const [consent, setConsent] = useState(false);
  const pad = useRef<SignaturePadHandle | null>(null);
  const [hasSigned, setHasSigned] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = useMutation({
    mutationFn: async () => {
      const trimmed = confirmName.trim();
      if (!trimmed) throw new Error('Please type your full name to confirm');
      if (!hasSigned && active.requireSignature !== false) throw new Error('Please draw your signature');
      if (!consent) throw new Error('Please tick the box to agree to sign electronically');

      const matchByName = workers.find((w) => w.name.toLowerCase() === trimmed.toLowerCase());
      const workerId = matchByName?.id ?? auth.user?.workerId ?? null;
      if (!workerId) throw new Error('Name does not match any registered worker');

      const recipient = active.recipients.find((r) => r.workerId === workerId);
      if (!recipient) throw new Error(`This document was not shared with ${matchByName?.name ?? trimmed}`);
      if (recipient.status === 'signed') throw new Error(`${matchByName?.name ?? trimmed} has already signed this document`);

      const signatureData = hasSigned ? pad.current?.toDataURL() : '';
      await api.post(`/shared-docs/sign/${recipient.id}`, { signatureData, confirmName: trimmed, consent });
      return { workerName: matchByName?.name ?? trimmed };
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['shared-docs'] });
      toast.success('Document Signed', `✓ ${res.workerName} signed ${active.name}`);
      onClose();
    },
    onError: (e: any) => toast.warn('Sign failed', e?.message ?? 'Check name + signature and try again'),
  });

  const sharedAtLabel = new Date(active.sharedAt ?? active.createdAt ?? Date.now()).toLocaleDateString('en-GB');
  const sharedBy = active.createdByName ?? active.sharedBy ?? '—';

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 680 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title" id="wsign-title">📄 {active.name}</div>
        <div className="modal-sub" id="wsign-sub">
          {active.projectId ?? ''}{active.projectId ? ' · ' : ''}Shared by {sharedBy} on {sharedAtLabel}
        </div>
        <div id="wsign-doc-content" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, maxHeight: 200, overflowY: 'auto', marginBottom: 14, fontSize: 12, lineHeight: 1.7, color: 'var(--text)' }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{active.name}</div>
          {active.docType || active.docRef ? (
            <div style={{ fontSize: 10, color: 'var(--accent)', marginBottom: 8 }}>
              {active.docType ?? ''}{active.docRef ? ` · Ref: ${active.docRef}` : ''}
            </div>
          ) : null}
          {active.docNotes ? <p style={{ marginBottom: 8 }}>{active.docNotes}</p> : null}
          <p style={{ color: 'var(--muted)' }}>Please read this document in full before signing. By signing below you confirm that you have read, understood and agree to the contents of this document.</p>
        </div>
        {active.message ? (
          <div id="wsign-message" style={{ background: 'var(--accent-dim)', border: '1px solid rgba(232,164,39,.3)', borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 12 }}>
            <strong>Message from {sharedBy}:</strong> {active.message}
          </div>
        ) : null}
        <div className="form-row">
          <label className="form-label">Your Full Name (type to confirm)</label>
          <input type="text" className="form-input" id="wsign-name-confirm" placeholder="Type your full name to confirm you have read this document" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} />
        </div>
        {active.requireSignature !== false ? (
          <div className="form-row">
            <label className="form-label">Signature — draw below</label>
            <SignaturePad ref={pad} onInk={setHasSigned} />
          </div>
        ) : <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>This document only needs an acknowledgement — type the name and confirm.</div>}
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, marginBottom: 10, cursor: 'pointer' }}>
          <input type="checkbox" id="wsign-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 2 }} />
          <span>I agree to sign this document electronically and I understand that my electronic signature is as binding as a handwritten one.</span>
        </label>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" id="wsign-confirm-btn" onClick={() => submit.mutate()} disabled={submit.isPending}>
            {submit.isPending ? 'Submitting…' : '✍ Sign & Submit'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
